import 'mission-control-common-components/dist/static/css/main.css';

import envConfig from '@hulu/env-config';
import { Navigate, Route, Routes, useLocation } from '@hulu/react-router-dom';
import { notification } from 'antd';
import type { NotificationItem } from 'mission-control-common-components';
import { User } from 'mission-control-common-components';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { AIAssistantMount } from './common/AIAssistant/AIAssistantMount';
import { LOCATION_BEFORE_LOGIN } from './common/constants';
import Footer from './common/Footer';
import Header from './common/Header';
import LDClientBridge from './common/LDClientBridge/LDClientBridge';
import Loader from './common/Loader';
import { sendTokenToWorker } from './configs/serviceWorkerInit';
import { PathName, requiredPermissions } from './constants';
import FilterProvider from './contexts/FilterContext';
import { useGetRoleDefinitions, useLogoutOnCognitoPoolFlagChange } from './hooks';
import AdsPageController from './pages/Ads';
import ForbiddenPage from './pages/Forbidden';
import NotFoundPage from './pages/NotFound';
import OauthCallback from './pages/OauthCallback';
import ReportsPageController from './pages/Reports';
import SequencePageWrapper from './pages/Sequence/SequencePageWrapper';
import ServiceDownPage from './pages/ServiceDown';
import SourceAdIds from './pages/SourceAdIds';
import TraffickingPageController from './pages/Trafficking';
import configuration, { pricingManagerConfig } from './plugin/configuration';
import RefreshComponent from './plugin/refreshComponent';
import { getCognitoDetailsForLogin, getCognitoDetailsForSession } from './utils/cognitoDetails';
import { getNotificationsAccessToken } from './utils/getNotificationsAccessToken';
import { combinePlugins, traffickingRouteElement } from './utils/plugins';

const fallback = <Loader className={'main'} />;

/** Pricing lazy component can't be created inside a loop in this same function as React.Suspense because loosing reference to the Router */
const PricingManagerLazy = React.lazy(pricingManagerConfig.getComponent);

const App: React.FC<{}> = () => {
  const [idTokenChecked, setIdTokenChecked] = useState({ checked: false, isChecking: true });
  const isLoggingInRef = useRef(false);
  const isAuthEnabled: boolean = envConfig.REACT_APP_AUTH_FEATURE_ENABLED === 'true';

  const { data: roleDefinitions, loading: roleDefinitionsLoading } = useGetRoleDefinitions();

  const permissions = User.generateUserPermissions(isAuthEnabled, requiredPermissions, roleDefinitions);

  const notificationsProp = useMemo(
    () => ({
      getAccessToken: getNotificationsAccessToken,
      notificationsApiUrl: envConfig.REACT_APP_NOTIFICATIONS_API_URL,
      onNotificationNavigate: (
        href: string,
        _notification: NotificationItem,
        event: Pick<MouseEvent, 'metaKey' | 'ctrlKey' | 'button' | 'shiftKey' | 'altKey'>
      ): void => {
        if (event.ctrlKey || event.metaKey) {
          window.open(href, '_blank');
          return;
        }
        window.location.assign(href);
      },
    }),
    []
  );

  const location = useLocation();
  const isUserAuthenticated = isAuthEnabled ? User.getUserInfo().idToken : true;
  const isCurrentPageSecure = location.pathname !== PathName.oauthCallback && location.pathname !== PathName.forbidden;

  useLogoutOnCognitoPoolFlagChange({
    isAuthEnabled,
  });

  useEffect(() => {
    notification.destroy();
  }, [location.pathname]);

  useEffect(() => {
    if (idTokenChecked.checked) {
      return;
    }

    if (!isAuthEnabled) {
      setIdTokenChecked({ checked: true, isChecking: false });

      return;
    }
    if (location.pathname === PathName.oauthCallback || location.pathname === PathName.forbidden) {
      setIdTokenChecked({ checked: true, isChecking: false });

      return;
    }

    const { idToken } = User.getUserInfo();
    if (User.isIdTokenValid(idToken)) {
      setIdTokenChecked({ checked: true, isChecking: false });

      return;
    }

    setIdTokenChecked({ checked: true, isChecking: true });
    getCognitoDetailsForSession()
      .then((cognitoDetails) => User.checkIdToken(undefined, cognitoDetails))
      .then(() => {
        sendTokenToWorker(navigator);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIdTokenChecked({ checked: true, isChecking: false });
      });
  }, [setIdTokenChecked, idTokenChecked, location.pathname, isAuthEnabled]);

  // Display the loader until role definitions are available
  if (roleDefinitionsLoading || !idTokenChecked) return fallback;

  if (!isUserAuthenticated && isCurrentPageSecure) {
    if (!isLoggingInRef.current) {
      isLoggingInRef.current = true;
      localStorage.setItem(LOCATION_BEFORE_LOGIN, JSON.stringify(location));
      getCognitoDetailsForLogin().then((cognitoDetails) => User.login(cognitoDetails));
    }
    return fallback;
  }

  if (isUserAuthenticated) {
    const item = localStorage.getItem(LOCATION_BEFORE_LOGIN);

    if (item) {
      localStorage.removeItem(LOCATION_BEFORE_LOGIN);
      try {
        return <Navigate to={JSON.parse(item)} replace />;
      } catch {
        // Ignore malformed saved location (e.g. legacy or corrupted localStorage values).
      }
    }
  }

  return (
    <div id="app">
      <LDClientBridge />
      <AIAssistantMount>
        <Header isAuthEnabled={isAuthEnabled} permissions={permissions} notificationsProp={notificationsProp} />
        <FilterProvider>
          <Routes>
            <Route path={PathName.reports} element={<ReportsPageController />} />
            <Route path={PathName.forbidden} element={<ForbiddenPage />} />
            <Route path={PathName.serviceDown} element={<ServiceDownPage />} />
            <Route path={PathName.oauthCallback} element={<OauthCallback />} />
            <Route
              path={PathName.trafficking}
              element={traffickingRouteElement(permissions?.traffickingPageRead, <TraffickingPageController />)}
            />
            <Route
              path={PathName.adsEdit}
              element={traffickingRouteElement(permissions?.updateAd, <AdsPageController />)}
            />
            <Route
              path={PathName.adsCreate}
              element={traffickingRouteElement(permissions?.createAd, <AdsPageController />)}
            />
            <Route
              path={PathName.sequence}
              element={traffickingRouteElement(permissions?.createAd, <SequencePageWrapper />)}
            />
            <Route
              path={`${PathName.adLoadManager}/*`}
              element={traffickingRouteElement(permissions?.inventoryPageRead, <RefreshComponent />)}
            />
            <Route
              path={`${PathName.inventoryRestrictionManager}/*`}
              element={traffickingRouteElement(permissions?.inventoryPageRead, <RefreshComponent />)}
            />
            <Route
              path={`${PathName.markerCuration}/*`}
              element={traffickingRouteElement(permissions?.inventoryPageRead, <RefreshComponent />)}
            />
            <Route
              path={`${PathName.targetingPreset}/*`}
              element={traffickingRouteElement(permissions?.presetPageRead, <RefreshComponent />)}
            />
            <Route
              path={`${PathName.inventoryBlock}/*`}
              element={traffickingRouteElement(permissions?.readInventoryBlock, <RefreshComponent />)}
            />
            <Route
              path={`${PathName.campaignReview}/*`}
              element={traffickingRouteElement(permissions?.campaignReviewPageRead, <RefreshComponent />)}
            />
            <Route
              path={`${PathName.campaignReviewer}/*`}
              element={traffickingRouteElement(permissions?.campaignReviewPageRead, <RefreshComponent />)}
            />
            {/* Asset 2 Review UI will take care of permissions */}
            <Route path={`${PathName.asset2Review}/*`} element={traffickingRouteElement(true, <RefreshComponent />)} />
            {/* MC Portal will take care of permissions for any plugins on the test path */}
            <Route path={PathName.mcportalStaging} element={traffickingRouteElement(true, <RefreshComponent />)} />
            {/* Hotfix path to display source ad ids */}
            <Route path={PathName.sourceAdIds} element={<SourceAdIds />} />

            <Navigate to={PathName.trafficking} />
            {combinePlugins(configuration, permissions)}
            <Route
              path={`${pricingManagerConfig.route}/*`}
              element={traffickingRouteElement(
                permissions?.[pricingManagerConfig.permissionName],
                <React.Suspense fallback={fallback}>
                  <PricingManagerLazy />
                </React.Suspense>
              )}
            />
            <Route
              path={`${PathName.demandManagement}/*`}
              element={traffickingRouteElement(permissions?.demandManagementPageRead, <RefreshComponent />)}
            />
            <Route
              path={`${PathName.supplyManagement}/*`}
              element={traffickingRouteElement(permissions?.supplyManagementPageRead, <RefreshComponent />)}
            />
            <Route
              path={PathName.contentMetaData}
              element={traffickingRouteElement(permissions?.traffickingPageRead, <RefreshComponent />)}
            />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </FilterProvider>
        <Footer />
      </AIAssistantMount>
    </div>
  );
};

export default App;
